import { useState, useEffect, useCallback, useRef } from 'react';
import {
  createTournament,
  updateTournament,
  mergeTournament,
  deleteTournament,
  getTournament,
  // getUserTournaments,
  getTournamentByShareUrl,
  subscribeToTournament,
  subscribeToUserTournaments,
  duplicateTournament,
  saveTournamentFromLocal,
  subscribeToInstance,
  getInstance
} from '../firebase/tournaments';
import { useAuth } from '../contexts/AuthContext';
import { applyBracketLogicWithPropagation } from '../utils/tournamentManager';
import { readBracketAsArray, bracketArrayToMap } from '../utils/bracketSchema';

// Wire the full set of "the page just came back to the foreground" signals to a
// handler, and return a cleanup that removes them all.
//
// Why all four: a phone waking from sleep, or an installed PWA that Chrome froze
// while pocketed, does NOT reliably fire the same event every time. `focus` and
// `visibilitychange` cover a normal tab/app switch; `online` covers a dropped
// connection coming back; `pageshow` is the one that fires when the page is
// restored from the back/forward cache (the common installed-PWA resume path),
// where the others may stay silent. Missing `pageshow` was why the viewer sat on
// stale data until a manual refresh. The handler is only invoked when the page is
// actually visible, so a background `online`/`pageshow` doesn't fetch needlessly.
const addResumeListeners = (handler) => {
  const onResume = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    handler();
  };
  const onVisibility = () => { if (document.visibilityState === 'visible') handler(); };
  window.addEventListener('focus', onResume);
  window.addEventListener('online', onResume);
  window.addEventListener('pageshow', onResume);
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    window.removeEventListener('focus', onResume);
    window.removeEventListener('online', onResume);
    window.removeEventListener('pageshow', onResume);
    document.removeEventListener('visibilitychange', onVisibility);
  };
};

// Propagate winners on a single occurrence's bracket and re-map it, so spectators
// (and the director) always see a consistent bracket even if a write landed
// without propagation. Idempotent — safe to run on every snapshot.
const processInstanceData = (instance, fallbackFormat) => {
  if (!instance) return instance;
  const bracket = readBracketAsArray(instance);
  if (bracket.length === 0) return instance;
  const processed = applyBracketLogicWithPropagation(bracket, instance.format || fallbackFormat);
  const { bracketMap, matchOrder } = bracketArrayToMap(processed);
  return { ...instance, bracketMap, matchOrder };
};

// Custom hook for managing a single tournament.
// `selectedInstanceDate` (optional) subscribes to that occurrence's subcollection
// document so a live score update only re-downloads that ~19 KB doc, never the
// whole parent (which now carries just metadata + a tiny `instanceIndex`).
export const useTournament = (tournamentId, selectedInstanceDate = null) => {
  const [tournament, setTournament] = useState(null);
  const [activeInstance, setActiveInstance] = useState(null);
  // Firestore write-ack metadata for the active occurrence doc. `hasPendingWrites`
  // true = this client has edits committed to the local cache that the server has
  // NOT yet acknowledged — the signal a truthful sync indicator needs (a green
  // "synced" LED that only reflects "no error thrown" lies while offline).
  const [instanceMeta, setInstanceMeta] = useState({ hasPendingWrites: false, fromCache: false });
  // Last SERVER-CONFIRMED occurrence snapshot (delivered with hasPendingWrites=false,
  // i.e. no un-acked local writes sitting on top). This — NOT the optimistic
  // activeInstance — is the correct diff baseline: diffing against the optimistic
  // cache (which echoes the director's own un-acked edits) makes a forced re-sync
  // produce an EMPTY diff and silently fail to re-push edits stuck offline.
  const [serverInstance, setServerInstance] = useState(null);
  const [instanceLoading, setInstanceLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Track whether real-time updates are active
  const [isRealtimeActive, setIsRealtimeActive] = useState(true);
  // Track previous bracket state to detect newly finished matches
  const prevBracketStateRef = useRef({});

  // Helper function to process tournament data and propagate winners
  const processTournamentData = useCallback((tournamentData) => {
    if (!tournamentData) return tournamentData;

    // Process each instance's bracket to propagate winners
    if (tournamentData.instances) {
      const processedInstances = { ...tournamentData.instances };
      let anyChanges = false;

      Object.keys(processedInstances).forEach(instanceKey => {
        const instance = processedInstances[instanceKey];
        if (instance?.bracket && instance.bracket.length > 0) {
          const format = instance.format || tournamentData.format;

          // Check for newly finished matches
          const prevState = prevBracketStateRef.current[instanceKey] || {};
          let hasNewlyFinishedMatch = false;

          instance.bracket.forEach(match => {
            const prevMatch = prevState[match.id];
            if (match.finished && (!prevMatch || !prevMatch.finished)) {
              hasNewlyFinishedMatch = true;
            }
          });

          // Update prev state for next comparison
          const newPrevState = {};
          instance.bracket.forEach(match => {
            newPrevState[match.id] = { finished: match.finished };
          });
          prevBracketStateRef.current[instanceKey] = newPrevState;

          // If a match was newly finished, apply tournament logic
          if (hasNewlyFinishedMatch) {
            const processedBracket = applyBracketLogicWithPropagation(instance.bracket, format);
            processedInstances[instanceKey] = {
              ...instance,
              bracket: processedBracket
            };
            anyChanges = true;
          }
        }
      });

      if (anyChanges) {
        return {
          ...tournamentData,
          instances: processedInstances
        };
      }
    }

    return tournamentData;
  }, []);

  useEffect(() => {
    if (!tournamentId) {
      setTournament(null);
      setLoading(false);
      return;
    }

    let unsubscribe;

    const loadTournament = async () => {
      try {
        setLoading(true);
        setError(null);

        // Try to get tournament by ID first
        const { tournament: tournamentData, error: getError } = await getTournament(tournamentId);

        if (getError) {
          // If not found by ID, try by share URL
          const { tournament: sharedTournament, error: shareError } = await getTournamentByShareUrl(tournamentId);

          if (shareError) {
            setError(shareError);
            setTournament(null);
          } else {
            setTournament(processTournamentData(sharedTournament));

            // Subscribe to real-time updates (handle permission errors gracefully)
            setIsRealtimeActive(true);
            unsubscribe = subscribeToTournament(
              sharedTournament.id,
              (updatedTournament) => {
                setTournament(processTournamentData(updatedTournament));
              },
              (subscriptionError) => {
                // If subscription fails due to permissions, keep the initially loaded data
                console.warn('Real-time updates unavailable for this tournament:', subscriptionError.message);
                setIsRealtimeActive(false);
                // Don't set error state - the tournament data is still valid
              }
            );
          }
        } else {
          setTournament(processTournamentData(tournamentData));

          // Subscribe to real-time updates (handle permission errors gracefully)
          setIsRealtimeActive(true);
          unsubscribe = subscribeToTournament(
            tournamentId,
            (updatedTournament) => {
              setTournament(processTournamentData(updatedTournament));
            },
            (subscriptionError) => {
              // If subscription fails due to permissions, keep the initially loaded data
              console.warn('Real-time updates unavailable for this tournament:', subscriptionError.message);
              setIsRealtimeActive(false);
              // Don't set error state - the tournament data is still valid
            }
          );
        }
      } catch (error) {
        console.error('Error loading tournament:', error);
        setError(error);
        setTournament(null);
      } finally {
        setLoading(false);
      }
    };

    loadTournament();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [tournamentId, processTournamentData]);

  // Subscribe to the selected occurrence's own document (subcollection schema).
  // Keyed on the RESOLVED tournament id (tournament?.id) so it also works when the
  // hook was loaded by share URL. No subscription (and no bracket bytes) while the
  // instance navigator is open (selectedInstanceDate === null).
  const resolvedId = tournament?.id;
  useEffect(() => {
    if (!resolvedId || !selectedInstanceDate) {
      setActiveInstance(null);
      setInstanceMeta({ hasPendingWrites: false, fromCache: false });
      setServerInstance(null);
      setInstanceLoading(false);
      return;
    }
    setInstanceLoading(true);
    const fallbackFormat = tournament?.format;
    let cancelled = false;
    const apply = (instance) => {
      if (!cancelled) setActiveInstance(instance ? processInstanceData(instance, fallbackFormat) : null);
    };
    const unsubscribe = subscribeToInstance(
      resolvedId,
      selectedInstanceDate,
      (instance, meta) => {
        apply(instance);
        if (meta && !cancelled) {
          setInstanceMeta(meta);
          // Only snapshots with no pending local writes reflect what the backend
          // actually holds — capture those as the server-confirmed baseline.
          if (!meta.hasPendingWrites) {
            setServerInstance(instance ? processInstanceData(instance, fallbackFormat) : null);
          }
        }
        setInstanceLoading(false);
      },
      (subscriptionError) => {
        console.warn('Real-time updates unavailable for this instance:', subscriptionError.message);
        setInstanceLoading(false);
      }
    );

    // Poor-connectivity resilience (esp. for spectators on weak venue wifi) and
    // resume-from-background: when the app returns to the foreground or the
    // network reconnects, force a fresh SERVER read of the occurrence so the
    // viewer catches up instantly instead of sitting on stale cache while the
    // realtime listen stream re-establishes. preferServer=true bypasses the
    // local cache, which can otherwise hand back the same stale doc on resume.
    const resync = async () => {
      try {
        const { instance } = await getInstance(resolvedId, selectedInstanceDate, true);
        if (instance) apply(instance);
      } catch (e) { /* listener will catch up on its own */ }
    };
    const removeResumeListeners = addResumeListeners(resync);

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
      removeResumeListeners();
    };
  }, [resolvedId, selectedInstanceDate, tournament?.format]);

  // Resume-resync for the PARENT tournament doc (metadata, instance index,
  // standings). The realtime listener lives in the load effect above; this
  // mirror keeps the parent fresh on foreground/reconnect the same way the
  // instance does. Keyed on the resolved id so it works when loaded by share URL.
  useEffect(() => {
    if (!resolvedId) return;
    const resync = async () => {
      try {
        const { tournament: fresh } = await getTournament(resolvedId, true);
        if (fresh) setTournament(processTournamentData(fresh));
      } catch (e) { /* listener will catch up on its own */ }
    };
    return addResumeListeners(resync);
  }, [resolvedId, processTournamentData]);

  const updateTournamentData = useCallback(async (updates) => {
    if (!tournament?.id) return { error: new Error('No tournament loaded') };

    const { error } = await updateTournament(tournament.id, updates);
    return { error };
  }, [tournament?.id]);

  const mergeTournamentData = useCallback(async (nestedUpdates) => {
    if (!tournament?.id) return { error: new Error('No tournament loaded') };

    const { error } = await mergeTournament(tournament.id, nestedUpdates);
    return { error };
  }, [tournament?.id]);

  return {
    tournament,
    activeInstance,
    serverInstance,
    instanceMeta,
    instanceLoading,
    loading,
    error,
    isRealtimeActive,
    updateTournament: updateTournamentData,
    mergeTournament: mergeTournamentData
  };
};

// Custom hook for managing user's tournaments
export const useUserTournaments = () => {
  const { uid, isAuthenticated } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !uid) {
      setTournaments([]);
      setLoading(false);
      return;
    }

    let unsubscribe;

    const loadTournaments = () => {
      try {
        setLoading(true);
        setError(null);

        // Subscribe to real-time updates
        unsubscribe = subscribeToUserTournaments(uid, (tournamentList) => {
          setTournaments(tournamentList);
          setLoading(false);
        });
      } catch (error) {
        console.error('Error loading user tournaments:', error);
        setError(error);
        setTournaments([]);
        setLoading(false);
      }
    };

    loadTournaments();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [uid, isAuthenticated]);

  const createNewTournament = useCallback(async (tournamentData) => {
    if (!uid) return { id: null, tournament: null, error: new Error('User not authenticated') };

    return await createTournament(tournamentData, uid);
  }, [uid]);

  const deleteTournamentById = useCallback(async (tournamentId) => {
    return await deleteTournament(tournamentId);
  }, []);

  const duplicateTournamentById = useCallback(async (tournamentId) => {
    if (!uid) return { id: null, tournament: null, error: new Error('User not authenticated') };

    return await duplicateTournament(tournamentId, uid);
  }, [uid]);

  const saveFromLocal = useCallback(async (localData) => {
    if (!uid) return { id: null, tournament: null, error: new Error('User not authenticated') };

    return await saveTournamentFromLocal(localData, uid);
  }, [uid]);

  const updateTournamentById = useCallback(async (tournamentId, updates) => {
    return await updateTournament(tournamentId, updates);
  }, []);

  return {
    tournaments,
    loading,
    error,
    createTournament: createNewTournament,
    deleteTournament: deleteTournamentById,
    duplicateTournament: duplicateTournamentById,
    updateTournament: updateTournamentById,
    saveFromLocal
  };
};

// Custom hook for tournament operations (CRUD)
export const useTournamentOperations = () => {
  const { uid } = useAuth();

  const create = useCallback(async (tournamentData) => {
    if (!uid) return { id: null, tournament: null, error: new Error('User not authenticated') };

    return await createTournament(tournamentData, uid);
  }, [uid]);

  const update = useCallback(async (tournamentId, updates) => {
    return await updateTournament(tournamentId, updates);
  }, []);

  const remove = useCallback(async (tournamentId) => {
    return await deleteTournament(tournamentId);
  }, []);

  const duplicate = useCallback(async (tournamentId) => {
    if (!uid) return { id: null, tournament: null, error: new Error('User not authenticated') };

    return await duplicateTournament(tournamentId, uid);
  }, [uid]);

  const get = useCallback(async (tournamentId) => {
    return await getTournament(tournamentId);
  }, []);

  const getByShareUrl = useCallback(async (shareUrl) => {
    return await getTournamentByShareUrl(shareUrl);
  }, []);

  return {
    create,
    update,
    remove,
    duplicate,
    get,
    getByShareUrl
  };
};